import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import '../../assets/styles/AdminShared.css';

const questions = [
  "Is the client portal easy to navigate and understand?",
  "Can you successfully log in and log out without issues?",
  "Is the dashboard displaying accurate project and order information?",
  "Do all buttons and links in the portal work correctly?",
  "Are forms (e.g., document uploads, order creation) functioning properly?",
  "Does the portal work well on both desktop and mobile devices?",
  "Are notifications, messages, or updates displaying as expected?",
  "Does the design and layout feel professional and consistent with the brand?",
  "Is any section of the client portal confusing or hard to use?",
  "What improvements or features would you like to see added?",
  "Did you encounter any errors or bugs during your experience?",
  "Is there anything missing you expected to find?",
  "Any additional comments or feedback?"
];

function AdminClientPortalRevision() {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("Loading...");
  const [responses, setResponses] = useState({});
  const [revisionLink, setRevisionLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!projectId) throw new Error("Project ID is missing.");
        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const data = projectSnap.data();
          setProjectName(data.projectName || "Unnamed Project");
          setResponses(data.revisionDetails?.clientPortalRevision?.form || {});
          setRevisionLink(data.revisionDetails?.clientPortalRevision?.link || "");
        } else {
          throw new Error("Project not found.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const handleInputChange = (question, value) => {
    setResponses((prev) => ({ ...prev, [question]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        "revisionDetails.clientPortalRevision.form": responses,
        "revisionDetails.clientPortalRevision.link": revisionLink,
      });
      alert("Responses and link updated successfully!");
    } catch (err) {
      setError("Failed to update: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href="/admin" className="ap-back">← Back to dashboard</a>
        <p className="ap-eyebrow">Admin — Revision</p>
        <h1 className="ap-title">{projectName}: Client Portal Revision</h1>
        <p className="ap-subtitle">View or edit the client's responses for the client portal revision.</p>
      </div>

      {loading ? (
        <div className="ap-loading"><p>Loading...</p></div>
      ) : error ? (
        <div className="ap-loading ap-loading--error"><p>{error}</p></div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="ap-section" style={{ marginBottom: '24px' }}>
            <div className="ap-section-header">
              <h2 className="ap-section-title">Revision Link</h2>
            </div>
            <div className="ap-section-body">
              <div className="ap-form-group">
                <label>Client Portal Link</label>
                <input
                  type="text"
                  value={revisionLink}
                  onChange={(e) => setRevisionLink(e.target.value)}
                  placeholder="Enter or update the client portal link"
                />
              </div>
            </div>
          </div>

          <div className="ap-section">
            <div className="ap-section-header">
              <h2 className="ap-section-title">Client Responses</h2>
            </div>
            <div className="ap-section-body">
              {questions.map((question, index) => (
                <div key={index} className="ap-form-group">
                  <label>{question}</label>
                  <textarea
                    value={responses[question] || ""}
                    onChange={(e) => handleInputChange(question, e.target.value)}
                    placeholder="Enter response here..."
                  ></textarea>
                </div>
              ))}
            </div>
          </div>

          <div className="ap-btn-row" style={{ marginTop: '24px' }}>
            <button type="submit" className="ap-btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default AdminClientPortalRevision;
